/**
 * Fetches conversation history from the AG-UI GET endpoint and applies it
 * to the assistant-ui runtime via `runtime.thread.reset()`.
 *
 * The backend returns a JSON array of AG-UI Message objects (UserMessage,
 * AssistantMessage, ToolMessage). We convert them into the ThreadMessageLike
 * format that assistant-ui expects.
 *
 * Key structural mapping: during streaming, the AG-UI runtime builds ONE
 * assistant ThreadMessage per user turn with all tool-call parts merged in.
 * The backend history returns SEPARATE AssistantMessages per model response.
 * We merge them here.
 */
import { useEffect, useState } from "react";
import type { AssistantRuntime, ThreadMessageLike } from "@assistant-ui/react";
import type { AssistantMessageCustomMetadata, TurnFeedback } from "~/components/assistant/types";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

interface AguiToolCall {
  id: string;
  type: string;
  function: { name: string; arguments: string };
}

interface AguiMessage {
  role: "user" | "assistant" | "tool";
  content: string | null;
  /** Message UUID from database (present on all messages from history). */
  id?: string;
  toolCalls?: AguiToolCall[];
  toolCallId?: string;
  /** Artifact names to resolve via /artifacts/resolve (from final_result). */
  artifactNames?: string[];
  /** True when this message represents a cancelled agent run */
  cancelled?: boolean;
  /** Turn ID (trace_id) for this turn — used for turn-level feedback. */
  turnId?: string;
  /** Existing turn-level feedback (from history). */
  turnFeedback?: TurnFeedback | null;
}

type ContentPart =
  | { readonly type: "text"; readonly text: string }
  | {
      readonly type: "tool-call";
      readonly toolCallId: string;
      readonly toolName: string;
      readonly args: Record<string, unknown>;
      readonly argsText: string;
      result?: string | null;
    };

/**
 * Converts AG-UI messages into ThreadMessageLike[], merging consecutive
 * assistant + tool messages into a single assistant message per user turn.
 *
 * The final response text arrives as a plain AssistantMessage with content.
 * Artifact names (if any) are stored in metadata for resolution by the
 * artifact rendering components.
 */
function convertAguiToThreadMessages(aguiMessages: AguiMessage[]): ThreadMessageLike[] {
  const result: ThreadMessageLike[] = [];
  let currentParts: ContentPart[] = [];
  // Track the last user message's turn ID + feedback so we can attach
  // them to the assistant message that follows (where the action bar lives).
  let lastTurnId: string | undefined;
  let lastTurnFeedback: TurnFeedback | null | undefined;
  // Set when a cancelled AssistantMessage is encountered, so the flushed
  // assistant message carries the cancelled flag in its metadata.
  let pendingCancelled = false;
  // Artifact names from the final-result message, stored in metadata.
  let pendingArtifactNames: string[] | undefined;

  const flushAssistant = () => {
    if (currentParts.length > 0 || pendingCancelled) {
      const custom: AssistantMessageCustomMetadata = {};
      if (lastTurnId) {
        custom.turnId = lastTurnId;
      }
      if (lastTurnFeedback) {
        custom.feedback = lastTurnFeedback;
      }
      if (pendingCancelled) {
        custom.cancelled = true;
      }
      if (pendingArtifactNames && pendingArtifactNames.length > 0) {
        custom.artifactNames = pendingArtifactNames;
      }
      result.push({
        role: "assistant",
        content: currentParts as ThreadMessageLike["content"],
        metadata: Object.keys(custom).length > 0 ? { custom } : undefined,
      });
      currentParts = [];
      pendingCancelled = false;
      pendingArtifactNames = undefined;
    }
  };

  for (const msg of aguiMessages) {
    if (msg.role === "user") {
      flushAssistant();
      lastTurnId = msg.turnId;
      lastTurnFeedback = msg.turnFeedback;
      result.push({
        role: "user",
        content: msg.content ?? "",
      });
    } else if (msg.role === "assistant") {
      // Cancelled message — flag it so the flushed assistant message
      // carries cancelled metadata. The AssistantMessage component
      // renders <CancellationMessage /> when it sees the flag.
      if (msg.cancelled) {
        pendingCancelled = true;
        continue;
      }

      // Standard tool calls (data_retriever, python_analyzer, etc.)
      if (msg.toolCalls) {
        for (const tc of msg.toolCalls) {
          let args: Record<string, unknown> = {};
          try {
            args = JSON.parse(tc.function.arguments);
          } catch {
            // keep empty args if parse fails
          }

          currentParts.push({
            type: "tool-call",
            toolCallId: tc.id,
            toolName: tc.function.name,
            args,
            argsText: tc.function.arguments,
          });
        }
      }

      // Final response text — render as a regular text part.
      if (msg.content && !msg.toolCalls) {
        currentParts.push({ type: "text", text: msg.content });
        if (msg.artifactNames && msg.artifactNames.length > 0) {
          pendingArtifactNames = msg.artifactNames;
        }
      }
    } else if (msg.role === "tool") {
      // Find the matching tool-call part and attach the result.
      // Search in reverse so we match the most recent tool call.
      for (let i = currentParts.length - 1; i >= 0; i--) {
        const part = currentParts[i];
        if (part.type === "tool-call" && part.toolCallId === msg.toolCallId) {
          part.result = msg.content;
          break;
        }
      }
    }
  }

  flushAssistant();
  return result;
}

export async function fetchConversationHistory(
  conversationId: string
): Promise<ThreadMessageLike[]> {
  const response = await fetch(
    `${VITE_ANALYSIS_AGENT_API}/conversations/${conversationId}/messages_agui`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch conversation history: ${response.status}`);
  }

  const rawMessages: AguiMessage[] = await response.json();

  if (!rawMessages.length) {
    return [];
  }

  return convertAguiToThreadMessages(rawMessages);
}

/**
 * Loads conversation history from the AG-UI GET endpoint when the conversation
 * changes, and applies it to the runtime via `reset()`.
 *
 * User messages in the loaded history carry `turnId` and `turnFeedback`,
 * which are passed through as assistant message metadata so the feedback
 * action bar can submit turn-level feedback.
 */
export function useConversationHistory(runtime: AssistantRuntime, conversationId: string | null) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!conversationId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    runtime.thread.reset();

    let cancelled = false;

    (async () => {
      try {
        const messages = await fetchConversationHistory(conversationId);
        if (cancelled) {
          return;
        }
        if (messages.length > 0) {
          runtime.thread.reset(messages);
        }
      } catch (error) {
        console.error("Failed to load conversation history:", error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId, runtime]);

  return { isLoadingHistory: isLoading };
}
